import dayjs from "dayjs";
import { isCancelled } from "./apptTypes";

/*
 * Scheduler rows (POST scheduler/schedule_list) before the grid draws them.
 *
 * canonicalRows: providername is appointment_master.linked_provider. The list
 * spells one provider two ways ("Dr. Madhu" / "Madhu Pamganamamula, MD") for
 * the same providerid, which used to split a provider into two columns; such a
 * spelling settles on the provider list's name for that id. But a name that IS
 * on the provider list is kept: staff reassign a visit to another provider on
 * purpose (linked_provider = Dr B, booked providerid = Dr A) and it belongs in
 * Dr B's column, as it always did. linked_provider keeps the raw value.
 *
 * visibleRows: the Providers / Type filters, then one row per appointment
 * (appointmentid, else the row id). Cancelled and no-show visits stay, drawn
 * struck through, except where a live visit sits in the same slot for the same
 * patient and provider (cancel and rebook, a reschedule clone): only the live
 * one is drawn, so a struck-through copy can never hide it. Two live visits in
 * one slot (a visit plus an ECG, a real double booking) both show.
 */
export function canonicalRows(scheduleData, providerOptions) {
  if (!Array.isArray(scheduleData)) return [];
  const nameById = {};
  (Array.isArray(providerOptions) ? providerOptions : []).forEach((p) => {
    if (p?.providerid != null && p?.providername) nameById[String(p.providerid)] = p.providername;
  });
  const known = new Set(Object.values(nameById));
  return scheduleData.map((a) => {
    const raw = a?.providername;
    const canonical = a?.providerid != null ? nameById[String(a.providerid)] : null;
    if (!canonical || canonical === raw || (raw && known.has(raw))) return a;
    return { ...a, providername: canonical, linked_provider: a.linked_provider ?? raw };
  });
}

const slotKey = (a) =>
  `${a.providername}|${a.patientid}|${dayjs(a.startdate).format("YYYY-MM-DD")}|${a.starttime}`;

export function visibleRows(rows, selectedProviders = null, selectedTypes = null) {
  const list = Array.isArray(rows) ? rows : [];
  const liveSlots = new Set(list.filter((a) => !isCancelled(a)).map(slotKey));
  const seen = new Set();
  return list.filter((a) => {
    if (selectedProviders !== null && !selectedProviders.includes(a.providername)) return false;
    if (selectedTypes !== null && !selectedTypes.includes(a.type)) return false;
    if (isCancelled(a) && liveSlots.has(slotKey(a))) return false;
    const id = a.appointmentid ?? a.AID ?? a.id;
    const key = id != null ? `id|${id}` : `slot|${slotKey(a)}|${a.visit_confirmation || ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
